import React, { useEffect, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import { useLayoutEffect, useReducer, useRef } from "react";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import type { ConnectionAnalyser } from "./ConnectionAnalyser";
import type SelectionManager from "../SelectionManager";
import { collectSelectedConnections, connectionWatchTargets } from "./collectSelectedConnections";
import { connectionCurves, connectionCurveCounts, connectionCurveShadowKeys, type ConnectionCurve } from "./connectionCurve";
import "./ConnectionAnalyserView.scss";
import { lazy, Suspense } from "react";
import { observePlanNavigation } from "./observePlanNavigation";
import { isConnectionAnalyserAvailable } from "./connectionAnalyserVisibility";
import { isVirtualConnection } from "./VirtualConnections";
import { captureConnectionNavigation, transformConnectionNavigation, clearConnectionNavigation } from "./connectionNavigationTransform";
import { measureInteractionRegion, unionInteractionRegions, connectionInteractionClip, connectionVisualRegions, interactionRegionPath, type InteractionRegion } from "./connectionInteractionRegions";

// The creation controller imports model factories. Load it after application
// initialization instead of pulling the model graph into early UI imports.
const ConnectionCreationOverlay = lazy(() => import("./ConnectionCreationOverlay").then((module) => ({
  default: module.ConnectionCreationOverlay,
})));

/** Restricts background blur to a soft band around the visible connection curves. */
function connectionBlurMask(curves: readonly ConnectionCurve[], width: number, height: number, left = 0, top = 0, regions: readonly InteractionRegion[] = []): string {
  const strokes: string[] = [];
  const masks: string[] = [];
  for (const curve of curves) {
    const maskId = `ends-${masks.length}`;
    const fades: string[] = [];
    for (const endpoint of curve.endpoints) {
      fades.push(`<circle cx="${endpoint.point.clientX}" cy="${endpoint.point.clientY}" r="36" fill="url(#endpointFade)"/>`);
    }
    masks.push(`<mask id="${maskId}" maskUnits="userSpaceOnUse" x="${left}" y="${top}" width="${width}" height="${height}" style="mask-type:luminance"><rect x="${left}" y="${top}" width="${width}" height="${height}" fill="white"/>${fades.join("")}</mask>`);
    const controlsId = `${maskId}-controls`;
    const controls = connectionVisualRegions(regions, curve.endpoints).map(region =>
      `<svg x="${region.left}" y="${region.top}" width="${region.right - region.left}" height="${region.bottom - region.top}" viewBox="${region.left} ${region.top} ${region.right - region.left} ${region.bottom - region.top}" overflow="hidden"><path d="${interactionRegionPath(region)}" fill="black"/></svg>`).join("");
    masks.push(`<mask id="${controlsId}" maskUnits="userSpaceOnUse" x="${left}" y="${top}" width="${width}" height="${height}" style="mask-type:luminance"><rect x="${left}" y="${top}" width="${width}" height="${height}" fill="white"/>${controls}</mask>`);
    strokes.push(`<g mask="url(#${controlsId})"><g filter="url(#soft)"><path d="${curve.path}" mask="url(#${maskId})"/></g></g>`);
  }
  // Black centers suppress blur near anchors; transparent edges restore it gradually.
  const endpointFade = '<radialGradient id="endpointFade"><stop offset="33%" stop-color="black"/><stop offset="100%" stop-color="black" stop-opacity="0"/></radialGradient>';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${left} ${top} ${width} ${height}"><defs>${endpointFade}${masks.join("")}<filter id="soft" filterUnits="userSpaceOnUse" x="${left}" y="${top}" width="${width}" height="${height}"><feGaussianBlur stdDeviation="1.5"/></filter></defs><g opacity="0.55" fill="none" stroke="white" stroke-width="14" stroke-linecap="round">${strokes.join("")}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** Selects a route and reveals its opposite side while preserving active dashboard trees. */
function selectAnalyserConnection(analyser: ConnectionAnalyser, selection: SelectionManager, curve: ConnectionCurve): void {
  const connection = analyser.connections.find((candidate) => candidate.id === curve.connectionId);
  if (!connection || connection.isDeleted) return;
  // Capture the active node before selecting the connection replaces selection.
  // Protect the whole column, including routes whose two ends share one tree.
  const selectedId = selection.selectedObject?.id;
  const protectedProviders = new Set(curve.endpoints.filter(endpoint =>
    endpoint.surface === "connection-dashboard" && endpoint.providerId && (
      endpoint.hoverElement?.matches(":hover, :focus-within, .is-selected, [data-connection-dashboard-hover], [data-connection-dashboard-latched]")
      || (selectedId && (endpoint.anchorObjectId === selectedId || endpoint.targetObjectId === selectedId))
    )).map(endpoint => endpoint.providerId));
  const revealEndpoints = curve.endpoints.filter(endpoint => !protectedProviders.has(endpoint.providerId));
  if (isVirtualConnection(connection)) {
    selection.selectVirtualConnection(connection, analyser);
  } else {
    analyser.selectedVirtualConnectionId.value = undefined;
    selection.setSelectedObjectWithoutNavigatorUpdate(connection);
  }
  analyser.bringEndpointsIntoView(revealEndpoints);
}

/** Owns the overlay lifetime and centrally replaces evidence when selection or layout changes. */
export function ConnectionAnalyserHost() {
  const analyser = state.connectionAnalyser;
  useOnChangeWatcher(state.c1.editMode);
  useOnChangeWatcher(state.activePanes);
  useOnChangeWatcher(analyser.manuallyEnabled);
  useOnChangeWatcher(analyser.hasConnectionSources);
  useOnChangeWatcher(analyser.portalTarget);
  useOnChangeWatcher(state.connectionDashboardMode);
  useEffect(
    () => () => {
      analyser.selectObject(undefined);
    },
    [analyser],
  );
  const mode = state.c1.editMode.value;
  const connectionDashboardActive = state.settings.applicationMode === "main" && state.connectionDashboardMode.value;
  const available = mode !== "EloConcept" && (connectionDashboardActive || analyser.hasConnectionSources.value || isConnectionAnalyserAvailable(
    state.settings.applicationMode,
    state.activePanes.getActiveItem()?.item?.className,
    mode,
    analyser.manuallyEnabled.value,
  ));
  if (connectionDashboardActive && !analyser.portalTarget.value) return null;
  return available ? (
    <ConnectionAnalyserView analyser={analyser} selection={state.selectionManager} mode={mode} portalTarget={connectionDashboardActive ? analyser.portalTarget.value : undefined} />
  ) : null;
}

export function ConnectionAnalyserView({
  analyser,
  selection,
  mode,
  portalTarget,
  allowCreation = true,
}: {
  analyser: ConnectionAnalyser;
  selection: SelectionManager;
  mode: string;
  portalTarget?: HTMLElement;
  allowCreation?: boolean;
}) {
  const overlayRef = useRef<SVGSVGElement>(null);
  const scrollClipId = useId();
  const terminalViaArrowId = useId();
  const interactionClipId = useId();
  const interactionMaskId = useId();
  const interactionRegions = useRef<InteractionRegion[]>([]);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [, redraw] = useReducer((value: number) => value + 1, 0);
  useEffect(() => {
    const updateFocus = () => redraw();
    document.addEventListener("focusin", updateFocus);
    document.addEventListener("focusout", updateFocus);
    return () => {
      document.removeEventListener("focusin", updateFocus);
      document.removeEventListener("focusout", updateFocus);
    };
  }, []);
  useOnChangeWatcher(analyser.endpoints);
  useOnChangeWatcher(analyser.displayedConnections);
  useOnChangeWatcher(analyser.selectedVirtualConnectionId);
  useOnChangeWatcher(analyser.navigationActive);
  useOnChangeWatcher(analyser.layoutResizing);
  useOnChangeWatcher(analyser.scrollingProviderIds);
  useLayoutEffect(() => {
    // The dashboard has its own independent column filters.
    analyser.setEditMode(portalTarget ? undefined : mode);
    let frame = 0;
    let disposeModels: (() => void)[] = [];
    const observedControls = new Set<Element>();
    const refresh = () => {
      frame = 0;
      if (analyser.layoutResizing.value || analyser.navigationActive.value || analyser.scrollingProviderIds.value.size) return;
      // Clip in viewport coordinates without moving the SVG or its connection anchors.
      const menuBottom = document.getElementById("menuBarWrapper")?.getBoundingClientRect().bottom ?? 0;
      const clipTop = portalTarget ? 0 : Math.max(0, Math.min(window.innerHeight, menuBottom));
      if (overlayRef.current) overlayRef.current.style.clipPath = `inset(${clipTop}px 0 0 0)`;
      if (backdropRef.current) backdropRef.current.style.clipPath = `inset(${clipTop}px 0 0 0)`;
      for (const dispose of disposeModels) dispose();
      disposeModels = [];
      const selected = analyser.selectedSource.value;
      analyser.setConnections(collectSelectedConnections(selected));
      const connections = analyser.connections;
      if (selection.selectedObject) selection.displaySelections.setConnectionTargets(analyser.expansionConnections.value, selection.selectedObject);
      // Connection colors can change while all endpoint coordinates remain identical.
      const controls = new Set((portalTarget ?? document).querySelectorAll('[data-connection-interaction="prefer-control"]'));
      for (const control of observedControls) if (!controls.has(control)) {
        interactionResize.unobserve(control); observedControls.delete(control);
      }
      for (const control of controls) if (!observedControls.has(control)) {
        observedControls.add(control); interactionResize.observe(control);
      }
      interactionRegions.current = [...controls].flatMap(control => {
        const rect = measureInteractionRegion(control);
        const planOwner = control.getAttribute("data-connection-interaction-plan-object");
        const planObjectIds = planOwner ? [planOwner, ...Array.from(control.querySelectorAll("[data-connection-object-id]"), child => child.getAttribute("data-connection-object-id")!).filter(Boolean)] : undefined;
        return rect ? [{...rect, planObjectIds}] : [];
      });
      redraw();
      const observed = connectionWatchTargets(selected, connections);
      for (const model of observed) {
        const handle = model.onChange.subscribe(schedule);
        disposeModels.push(() => model.onChange.unsubscribe(handle));
      }
    };
    const schedule = () => {
      if (analyser.layoutResizing.value || analyser.navigationActive.value || analyser.scrollingProviderIds.value.size) return;
      if (!frame) frame = requestAnimationFrame(refresh);
    };
    const navigation = observePlanNavigation((active) => {
      analyser.setNavigationActive(active);
      if (!active) schedule();
    });
    const zoomSubscription = state.c1.zoom.onChange.subscribe(navigation.activity);
    const interactionResize = new ResizeObserver(schedule);
    // SelectionManager owns the analysis source, including preserving it during deletion.
    const subscription = selection.onChange.subscribe(schedule);
    const sourceSubscription = analyser.selectedSource.onChange.subscribe(schedule);
    const scrollingSubscription = analyser.scrollingProviderIds.onChange.subscribe(schedule);
    if (!analyser.selectedSource.value && !analyser.hasConnectionSources.value) analyser.selectObject(selection.selectedObject);
    analyser.setViewVisible(true);
    refresh();
    window.addEventListener("resize", schedule);
    // ConnectionDashboard column scrolling is debounced by its owner; the outer viewport carries the SVG natively.
    const scroll = (event: Event) => {
      if (portalTarget?.matches(".connection-dashboard") && event.target instanceof Element
        && (event.target === portalTarget.parentElement || (portalTarget.contains(event.target) && event.target.matches(".connection-dashboard__scroller")))) return;
      schedule();
    };
    window.addEventListener("scroll", scroll, true);
    const menuResize = new ResizeObserver(schedule);
    if (portalTarget) menuResize.observe(portalTarget);
    const menu = document.getElementById("menuBarWrapper");
    if (menu) menuResize.observe(menu);
    const observer = new MutationObserver((mutations) => {
      let relevant = false;
      for (const mutation of mutations) {
        // Tree providers already publish layout changes. Hover cleanup during scrolling must not remeasure them.
        if (portalTarget?.matches(".connection-dashboard") && portalTarget.contains(mutation.target)) continue;
        relevant = true;
        if (mutation.type === "attributes" && mutation.target instanceof Element
          && mutation.target.matches(".page, #pageShadowWrapper") && mutation.attributeName === "style") {
          navigation.activity();
          break;
        }
      }
      if (relevant) schedule();
    });
    const app = document.getElementById("App") ?? document.getElementById("C1_App");
    if (app)
      observer.observe(app, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["style", "class", "hidden", "transform", "viewBox", "x", "y", "width", "height", "d", "cx", "cy", "r", "data-connection-cutout", "data-connection-object-id", "data-connection-exit-x", "data-connection-anchor", "data-connection-channel-anchors", "data-connection-interaction", "data-connection-interaction-plan-object"],
      });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      menuResize.disconnect();
      interactionResize.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", scroll, true);
      selection.onChange.unsubscribe(subscription);
      analyser.selectedSource.onChange.unsubscribe(sourceSubscription);
      analyser.scrollingProviderIds.onChange.unsubscribe(scrollingSubscription);
      for (const dispose of disposeModels) dispose();
      analyser.setViewVisible(false);
      analyser.setEditMode(undefined);
      state.c1.zoom.onChange.unsubscribe(zoomSubscription);
      navigation.dispose();
      cancelAnimationFrame(frame);
    };
  }, [analyser, selection, portalTarget, mode]);
  const allCurves = useMemo(() => connectionCurves(analyser.endpoints.value, analyser.connections), [analyser.endpoints.value, analyser.connections]);
  const curves = useMemo(() => analyser.layoutResizing.value ? [] : allCurves.filter(curve => !curve.endpoints.some(endpoint =>
    endpoint.providerId && analyser.scrollingProviderIds.value.has(endpoint.providerId))), [allCurves, analyser.scrollingProviderIds.value, analyser.layoutResizing.value]);
  const movingCurves = useRef<ReturnType<typeof captureConnectionNavigation>>([]);
  const connectionCounts = useMemo(() => connectionCurveCounts(curves), [curves]);
  const shadowKeys = useMemo(() => connectionCurveShadowKeys(curves), [curves]);
  useLayoutEffect(() => {
    if (!analyser.navigationActive.value && overlayRef.current) {
      clearConnectionNavigation(movingCurves.current);
      movingCurves.current = captureConnectionNavigation(overlayRef.current, curves);
    }
  });
  useLayoutEffect(() => {
    if (!analyser.navigationActive.value) return;
    let frame = 0;
    const move = () => {
      transformConnectionNavigation(movingCurves.current);
      frame = requestAnimationFrame(move);
    };
    move();
    return () => cancelAnimationFrame(frame);
  }, [analyser.navigationActive.value]);
  const bounds = portalTarget?.getBoundingClientRect();
  // Keep the coordinate origin paired with measured endpoints, even if a later hover render follows native scrolling.
  const origin = useMemo(() => ({left: bounds?.left ?? 0, top: bounds?.top ?? 0}), [portalTarget, analyser.endpoints.value]);
  const width = bounds?.width ?? window.innerWidth;
  const height = bounds?.height ?? window.innerHeight;
  const left = origin.left;
  const top = origin.top;
  const viewBox = bounds ? `${left} ${top} ${width} ${height}` : undefined;
  const localClass = portalTarget ? " connectionAnalyserLocal" : "";
  const interactionBounds = {left, top, right: left + width, bottom: top + height};
  const protectedRegions = unionInteractionRegions(interactionRegions.current, interactionBounds);
  const hitClip = protectedRegions.length ? `url(#${interactionClipId})` : undefined;
  const visualRegions = curves.map(curve => connectionVisualRegions(interactionRegions.current, curve.endpoints));
  const blurMask = connectionBlurMask(curves, width, height, left, top, interactionRegions.current);
  const paths = curves.map((curve, index) => (
    <g key={curve.key} data-connection-id={curve.connectionId} data-route-rule={curve.ruleId} data-lock-connection={curve.isLockConnection || undefined}
      data-scroll-clipped={curve.isScrollClipped || undefined}
      clipPath={curve.scrollBounds ? `url(#${scrollClipId}-${index})` : undefined}
      data-selected={curve.connectionId === (analyser.selectedVirtualConnectionId.value ?? selection.selectedObject?.id)}>
      {curve.scrollBounds && <defs><clipPath id={`${scrollClipId}-${index}`} clipPathUnits="userSpaceOnUse">
        <rect x={left} y={curve.scrollBounds.top} width={width} height={Math.max(0, curve.scrollBounds.bottom - curve.scrollBounds.top)} />
      </clipPath></defs>}
      <path className="connectionAnalyserLine" d={curve.path} stroke={curve.color} data-shadow={shadowKeys.has(curve.key)} mask={visualRegions[index].length ? `url(#${interactionMaskId}-${index})` : undefined}
        strokeDasharray={["DistributionHint", "TerminalVia"].includes(analyser.connections.find(connection => connection.id === curve.connectionId)?.type ?? "") ? "5 4" : undefined}
        markerEnd={analyser.connections.find(connection => connection.id === curve.connectionId)?.type === "TerminalVia" ? `url(#${terminalViaArrowId})` : undefined} />
      <path
        className="connectionAnalyserHitZone"
        clipPath={hitClip}
        d={curve.path}
        onPointerDown={(event) => {
          event.stopPropagation();
          // Keep the source focused until click selects the connection. Blurring a dashboard
          // node clears its selection/preview and can remove this hit zone before click.
          if (event.button === 0) event.preventDefault();
        }}
        onClick={(event) => {
          event.stopPropagation();
          selectAnalyserConnection(analyser, selection, curve);
        }}
        onDoubleClick={(event) => {
          event.stopPropagation();
          analyser.bringEndpointsIntoView(curve.endpoints, true);
        }}
      />
    </g>
  ));
  return createPortal(
    <>
      <div
        ref={backdropRef}
        className={`connectionAnalyser connectionAnalyserBackdrop${localClass}`}
        data-navigation-active={analyser.navigationActive.value}
        aria-hidden="true"
        style={{ maskImage: blurMask, WebkitMaskImage: blurMask, opacity: curves.length && !analyser.navigationActive.value ? 1 : 0 }}
      />
      <svg ref={overlayRef} className={`Theme connectionAnalyser${localClass}`} viewBox={viewBox} aria-hidden="true" data-navigation-active={analyser.navigationActive.value}>
        <defs>
          <clipPath id={interactionClipId} clipPathUnits="userSpaceOnUse">
            <path d={connectionInteractionClip(interactionBounds, protectedRegions)} clipRule="evenodd" style={{fill: "white", stroke: "none", opacity: 1}} />
          </clipPath>
          {visualRegions.map((regions, curveIndex) => <mask key={curveIndex} id={`${interactionMaskId}-${curveIndex}`} maskUnits="userSpaceOnUse" x={left} y={top} width={width} height={height} style={{maskType: "luminance"}}>
            <rect x={left} y={top} width={width} height={height} fill="white" />
            {regions.map((region, index) => <svg key={index} x={region.left} y={region.top}
              width={region.right - region.left} height={region.bottom - region.top}
              viewBox={`${region.left} ${region.top} ${region.right - region.left} ${region.bottom - region.top}`} overflow="hidden">
              <path d={interactionRegionPath(region)} style={{ fill: region.ellipse ? "black" : "#333", stroke: "none", opacity: 1 }} />
            </svg>)}
          </mask>)}
          <marker id={terminalViaArrowId} viewBox="0 0 10 10" refX="10" refY="5" markerWidth="10" markerHeight="10" markerUnits="userSpaceOnUse" orient="auto">
            <polygon points="0,0 10,5 0,10" fill="context-stroke" />
          </marker>
        </defs>
        {paths}
        {!analyser.navigationActive.value && connectionCounts.map(label => (
          <g key={label.key} clipPath={hitClip}>
          <g className="connectionAnalyserCount" data-connection-count={label.count}
            transform={`translate(${label.x} ${label.y})`}>
            <title>{`${label.count} Verbindungen an dieser Node-Seite`}</title>
            <circle r={label.radius} />
            <text textAnchor="middle" dominantBaseline="central">{label.count}</text>
          </g>
          </g>
        ))}
        <Suspense fallback={null}>
          {allowCreation && !analyser.navigationActive.value && !analyser.layoutResizing.value && <ConnectionCreationOverlay analyser={analyser} mode={mode} />}
        </Suspense>
      </svg>
    </>,
    // Global connection overlays belong to the workspace stacking context.
    // A body portal can escape it and paint over DialogHost's global dialogs.
    portalTarget ?? document.getElementById("applicationWorkspace") ?? document.body,
  );
}
